import React, { useState } from 'react';
import { BearerToken } from '@marageti/z4-sdk/lib/auth';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { LoginRequest, GoogleLoginRequest } from '@marageti/z4-sdk/lib/auth/Token';
import { useApiClient, LocalStorageService, hasAuthorizedRole } from '@marageti/z4-lib';
import LoginView from './login-view';
import { useAuthStore } from '../../store';
import { getMatchingUser, authorizedRoles } from '../../utils/auth';

const ENV = process.env.REACT_APP_STAGE;

const LocalStorage = new LocalStorageService();

const LoginContainer = () => {
  const { setIsAuthenticated, setAuthenticatedTeamMember, removeAuthenticatedTeamMember } = useAuthStore();
  const apiClient = useApiClient();

  const [loginError, setLoginError] = useState<ApiError>();
  const [loading, setLoading] = useState(false);

  const onSubmit = (loginRequest: LoginRequest | GoogleLoginRequest): void => {
    setLoginError(undefined);
    setLoading(true);

    // Prevents non zicasso emails from being used
    if (ENV !== 'prod' && 'username' in loginRequest && !loginRequest.username.includes('zicasso')) {
      setIsAuthenticated(false);
      setLoginError({
        id: '',
        code: 401,
        message: 'Unauthorized',
      });
      setLoading(false);
      return;
    }

    apiClient.authClient.login(loginRequest)
      .then(async (data: BearerToken) => {
        apiClient.instance.setAuthToken(data.accessToken);
        const token = await apiClient.authClient.getToken(data.accessToken);

        LocalStorage.addToLocalStorage('accessToken', data.accessToken);
        LocalStorage.addToLocalStorage('email', token.email);
        if (token.superEmail) {
          LocalStorage.addToLocalStorage('superEmail', token.superEmail);
        }

        return apiClient.peopleClient.getTeamMember(token.email);
      })
      .then((data: TeamMember) => {
        const matchingUser = getMatchingUser(data.email, data.userInfo);
        if (!matchingUser || !hasAuthorizedRole(matchingUser.roles, authorizedRoles)) {
          throw new Error('Not Authorized');
        }
        setAuthenticatedTeamMember(data);
        setIsAuthenticated(true);
      })
      .catch((e: ApiError) => {
        removeAuthenticatedTeamMember();
        setIsAuthenticated(false);
        setLoginError(e);
      })
      .finally(() => setLoading(false));
  };

  return (
    <LoginView
      loading={loading}
      loginError={loginError}
      onSubmit={onSubmit}
    />
  );
};

export default LoginContainer;
